// Small stand-ins for built-ins that the portal or its libraries call without a fallback, so it
// still runs on older phones: Safari 12.2+ (iPhone 5s and 6 stop at iOS 12) and Chrome or
// Android WebView 66+.
// Each one is installed only where the browser lacks it. Imported first in index.js.

// Safari 15.4, Chrome 93: react-markdown's JSX runtime.
export const hasOwn = (object, key) => {
  if (object === null || object === undefined) throw new TypeError("Cannot convert undefined or null to object");
  return Object.prototype.hasOwnProperty.call(object, key);
};

// Safari 12.1, Chrome 73: explore filters, documents and the brand palette.
export const fromEntries = (entries) =>
  Array.from(entries).reduce(
    // defineProperty, not assignment, so a "__proto__" key stays an own key as it does natively.
    (result, [key, value]) => Object.defineProperty(result, key, { value, enumerable: true, writable: true, configurable: true }),
    {}
  );

// Chrome 69 (older Android WebViews): date pickers and the CV check wizard. Called as methods.
export function flat(depth) {
  const levels = depth === undefined ? 1 : Number(depth);
  const flatten = (items, remaining) =>
    Array.prototype.reduce.call(
      items,
      (result, item) => (Array.isArray(item) && remaining >= 1 ? result.concat(flatten(item, remaining - 1)) : result.concat([item])),
      []
    );
  return flatten(this, levels);
}

export function flatMap(callback, thisArg) {
  return flat.call(Array.prototype.map.call(this, callback, thisArg), 1);
}

// Safari 13, Chrome 76: the home page loads its cards with it.
export const allSettled = (values) =>
  Promise.all(
    Array.from(values, (value) =>
      Promise.resolve(value).then(
        (result) => ({ status: "fulfilled", value: result }),
        (reason) => ({ status: "rejected", reason })
      )
    )
  );

// Safari 12.1, Chrome 71: SurveyJS debounces with it.
export const queueMicrotaskShim = (callback) => {
  Promise.resolve()
    .then(callback)
    .catch((error) =>
      setTimeout(() => {
        throw error;
      })
    );
};

// Safari 13.1: SurveyJS and the popover positioning observe element sizes. This stand-in checks
// sizes when the window resizes: each target is reported once after observe() and then only when
// its border-box size has changed (native reports the content box).
export class WindowResizeObserver {
  constructor(callback) {
    this.callback = callback;
    this.targets = [];
    this.sizes = new Map();
    this.notify = this.notify.bind(this);
  }

  observe(target) {
    if (this.targets.indexOf(target) !== -1) return;
    this.targets = this.targets.concat(target);
    if (this.targets.length === 1) window.addEventListener("resize", this.notify);
    window.requestAnimationFrame(this.notify);
  }

  unobserve(target) {
    this.targets = this.targets.filter((observed) => observed !== target);
    this.sizes.delete(target);
    if (!this.targets.length) window.removeEventListener("resize", this.notify);
  }

  disconnect() {
    this.targets = [];
    this.sizes.clear();
    window.removeEventListener("resize", this.notify);
  }

  notify() {
    const entries = this.targets
      .map((target) => ({ target, contentRect: target.getBoundingClientRect() }))
      .filter(({ target, contentRect }) => {
        const last = this.sizes.get(target);
        this.sizes.set(target, { width: contentRect.width, height: contentRect.height });
        return !last || last.width !== contentRect.width || last.height !== contentRect.height;
      });
    if (entries.length) this.callback(entries, this);
  }
}

const provide = (owner, name, value) => {
  if (typeof owner[name] !== "undefined") return;
  Object.defineProperty(owner, name, { value, configurable: true, writable: true });
};

export const installPolyfills = (scope) => {
  // Safari 12.1, Chrome 71: SurveyJS reads globalThis.document as it loads.
  provide(scope, "globalThis", scope);
  provide(Object, "hasOwn", hasOwn);
  provide(Object, "fromEntries", fromEntries);
  provide(Promise, "allSettled", allSettled);
  provide(Array.prototype, "flat", flat);
  provide(Array.prototype, "flatMap", flatMap);
  provide(scope, "queueMicrotask", queueMicrotaskShim);
  provide(scope, "ResizeObserver", WindowResizeObserver);
};

if (typeof window !== "undefined") installPolyfills(window);
